package presentation.screens

import analytics.Analytics
import com.kinoshazam.LANGUAGE
import com.kinoshazam.THEME
import com.kinoshazam.analytics.AppEvents
import com.kinoshazam.analytics.userIdFromJwt
import com.kinoshazam.data.GlobalStateRepository
import com.kinoshazam.data.Storage
import com.kinoshazam.data.TokenRepository
import com.kinoshazam.model.Profile
import com.kinoshazam.update.data.UpdateRepository
import crashreporting.CrashReporter
import data.ProfileRepository
import features.videoDetails.data.FavoriteInteractor
import features.watch.WatchProgressInteractor
import com.kinoshazam.taste.TasteInteractor
import io.github.aakira.napier.Napier
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.combine
import utils.BaseViewModel
import utils.launch
import kotlin.uuid.ExperimentalUuidApi

class RootViewModel(
    private val updateRepository: UpdateRepository,
    private val localStorage: Storage,
    private val profileRepository: ProfileRepository,
    private val globalStateRepository: GlobalStateRepository,
    private val favoriteInteractor: FavoriteInteractor,
    private val watchProgressInteractor: WatchProgressInteractor,
    private val tasteInteractor: TasteInteractor,
    private val analytics: Analytics,
    private val tokenRepository: TokenRepository,
    private val crashReporter: CrashReporter,
) : BaseViewModel<RootState, RootMVIAction>() {

    override fun initState(): RootState = RootState(
        local = localStorage.get<String>(LANGUAGE),
        theme = localStorage.get(THEME)
    )

    init {
        analytics.track(AppEvents.appOpened())
        analytics.flush()
        syncFavorites()
        syncWatchProgress()
        initConfigs()
        syncAnalyticsIdentity()
        launch {
            globalStateRepository.appLanguage.collect {
                analytics.register("language", it.locale)
                updateState { copy(local = it.locale) }
            }
        }
    }

    override fun onAction(action: RootMVIAction) {
        when (action) {
            is RootMVIAction.UpdateClicked -> {
                launch(onError = ::handleError) {
                    val version = stateValue.appUpdate?.versionName.orEmpty()
                    analytics.track(AppEvents.updateAccepted(version))
                    updateRepository.downloadUpdate()
                }
            }
        }
    }

    private fun syncFavorites() {
        launch {
            // Wait for profile (and thus a stable auth session) before syncing —
            // avoids racing getProfile with favorites on a fresh login (refresh-token rotation).
            var hadFavoriteSession = false
            combine(
                globalStateRepository.appLanguage,
                globalStateRepository.isAuthorized,
                globalStateRepository.profile,
            ) { _, auth, profile ->
                auth to profile
            }.collect { (authorized, profile) ->
                if (!authorized) {
                    // Cold start while logged out must keep device favorites.
                    // Only a real logout clears them, and it does not call the server.
                    if (hadFavoriteSession) {
                        runCatching { favoriteInteractor.clearOnLogout() }
                    }
                    hadFavoriteSession = false
                    return@collect
                }
                hadFavoriteSession = true
                if (profile == null) return@collect
                if (tokenRepository.getAccessToken()?.let(::userIdFromJwt) == null) return@collect
                launch {
                    runCatching { favoriteInteractor.syncFavorites() }
                        .onFailure { error ->
                            Napier.e("Favorites sync failed: ${error.message}")
                            crashReporter.capture(error)
                        }
                }
                launch {
                    runCatching { tasteInteractor.mergeDeviceOnLogin() }
                        .onFailure { error ->
                            Napier.e("Taste merge failed: ${error.message}")
                            crashReporter.capture(error)
                        }
                }
            }
        }
    }

    private fun syncWatchProgress() {
        launch {
            // Logout clears the previous account immediately. Login waits for profile
            // so the refresh-token rotation from a fresh session has finished.
            combine(
                globalStateRepository.appLanguage,
                globalStateRepository.isAuthorized,
                globalStateRepository.profile,
            ) { _, authorized, profile ->
                authorized to profile
            }.collect { (authorized, profile) ->
                if (!authorized) {
                    watchProgressInteractor.bindSession(null)
                    return@collect
                }
                val userId = tokenRepository.getAccessToken()?.let(::userIdFromJwt) ?: return@collect
                if (profile == null) {
                    watchProgressInteractor.discardIfForeign(userId)
                    return@collect
                }
                launch {
                    runCatching { watchProgressInteractor.bindSession(userId) }
                        .onFailure { error ->
                            Napier.e("Watch progress sync failed: ${error.message}")
                            crashReporter.capture(error)
                        }
                }
            }
        }
    }

    @OptIn(ExperimentalUuidApi::class)
    private fun initConfigs() {
        launch {
            globalStateRepository.isAuthorized.collect { isAuthorized ->
                if (!isAuthorized) {
                    globalStateRepository.setProfile(null)
                    return@collect
                }
                // Retry a few times — must not throw out of this collector.
                var loaded = false
                repeat(3) { attempt ->
                    if (!globalStateRepository.isAuthorized.value) return@collect
                    val result = runCatching { profileRepository.getProfile() }
                    result.onSuccess { profile ->
                        globalStateRepository.setProfile(
                            Profile(
                                userName = profile.userName,
                                email = profile.email,
                                avatar = profile.avatar,
                            )
                        )
                        loaded = true
                        return@repeat
                    }.onFailure { error ->
                        Napier.e("Failed to load profile (attempt ${attempt + 1}): ${error.message}")
                        crashReporter.capture(error)
                    }
                    if (attempt < 2) delay(400L * (attempt + 1))
                }
                if (!loaded) {
                    Napier.e("Profile load exhausted retries while authorized")
                }
            }
        }
        launch(onError = ::handleError) {
            updateRepository.getAppUpdate()?.let {
                globalStateRepository.setUpdate(it)
                updateState { copy(appUpdate = it) }
                if (it.isNeedUpdate) {
                    analytics.track(AppEvents.updateShown(it.versionName.orEmpty()))
                }
            }
        }

        // Resolve features for current session (anonymous = globals, logged-in = group overrides).
        // Re-fetch whenever auth flips so login/logout picks up entitlements.
        launch {
            globalStateRepository.isAuthorized.collect {
                runCatching { updateRepository.getAvailableFeatures() }
                    .onSuccess { features ->
                        features?.let { globalStateRepository.setFeatures(it) }
                    }
                    .onFailure { error ->
                        Napier.e("Failed to load features: ${error.message}")
                        crashReporter.capture(error)
                    }
            }
        }
    }

    private fun syncAnalyticsIdentity() {
        launch {
            globalStateRepository.isAuthorized.collect { authorized ->
                if (authorized) {
                    tokenRepository.getAccessToken()
                        ?.let(::userIdFromJwt)
                        ?.also { userId ->
                            analytics.identify(userId)
                            crashReporter.identify(userId)
                        }
                    analytics.setPersonProperties(mapOf("is_logged_in" to true))
                } else {
                    analytics.setPersonProperties(mapOf("is_logged_in" to false))
                    crashReporter.clearUser()
                }
            }
        }
    }

    private fun handleError(error: Throwable) {
        Napier.e(error.message ?: error.cause.toString())
    }
}
